import { usePathHelper, type AppLinkProps } from '@/hooks/usePathHelper'
import { CartSource } from '@/lib/enums/CartSource'
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { ShoppingCart } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import OrderSourceBadge from './OrderSourceBadge'
import { FulfillmentLinkCard } from './FulfillmentSurface'
import type {
  SalesOrderCalendarDay,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'
import type { ReactElement } from 'react'

export type CalendarOrder = Pick<
  SalesOrderListItem,
  'id' | 'purchaser' | 'source' | 'organization_id' | 'fulfillment_type' | 'fulfillment_due_date'
>

function MonthOrderCard({ order }: { order: CalendarOrder }): ReactElement {
  const path = usePathHelper()
  const purchaserName = order.purchaser?.name?.trim() || 'None provided'
  const isPosOrder = order.source === CartSource.POS.Value
  return (
    <FulfillmentLinkCard
      key={order.id}
      fulfillmentType={order.fulfillment_type}
      {...path.salesOrders.show(order.id).using({ orgId: order?.organization_id }).link}
      className="flex flex-col gap-1 px-2 py-1"
    >
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-1.5">
          <ShoppingCart className="size-3 shrink-0" />
          <Typography variant="caption" weight="medium" className="line-clamp-1">
            {purchaserName}
          </Typography>
        </div>
        {isPosOrder && <OrderSourceBadge source={order.source} size="xs" />}
      </div>
    </FulfillmentLinkCard>
  )
}

function getDayGridLinkProps(dayKey: string): AppLinkProps {
  return {
    search: (prev: Record<string, unknown>) => ({
      ...prev,
      due: dayKey,
      due_range: undefined,
      filter: prev.filter ?? 'all',
      layout: 'grid',
      month: undefined,
      page: 1,
      sort: undefined,
    }),
  }
}

function MonthOrderCountLink({ count, dayKey }: { count: number; dayKey: string }): ReactElement {
  const orderText = count === 1 ? 'order' : 'orders'

  return (
    <Badge
      asChild
      variant="brand"
      size="control-sm"
      shape="control"
      className="w-full justify-start"
    >
      <Link {...getDayGridLinkProps(dayKey)}>
        <ShoppingCart className="size-3 shrink-0" />
        <span className="truncate">
          {count} {orderText}
        </span>
      </Link>
    </Badge>
  )
}

export function OrdersMonthView({
  orderCounts = [],
  orders = [],
  dateRange,
  month,
  isLoading = false,
  showOrderCounts = false,
}: {
  orderCounts?: SalesOrderCalendarDay[]
  orders?: CalendarOrder[]
  dateRange?: string | null
  month?: string | null
  isLoading?: boolean
  showOrderCounts?: boolean
}): ReactElement {
  const today = dayjs()

  const monthAnchorDate = month ? `${month}-01` : dateRange?.split('to')[0]
  const startOfMonth = monthAnchorDate
    ? dayjs(monthAnchorDate).startOf('month')
    : today.startOf('month')
  // Get the first day of the week for the first day of the month
  const firstDayOfMonth = startOfMonth.day()
  const startDate = startOfMonth.subtract(firstDayOfMonth, 'day')

  // Create array of 42 days (6 weeks) to fill the calendar grid
  const calendarDays = Array.from({ length: 42 }, (_, i) => startDate.add(i, 'day'))
  const ordersWithDueDate = orders.filter((order) => Boolean(order.fulfillment_due_date))
  const orderCountsByDay = orderCounts.reduce<Record<string, number>>((acc, orderCount) => {
    acc[orderCount.date] = Number(orderCount.count ?? 0)

    return acc
  }, {})

  // Group orders by fulfillment date
  const ordersByDay = calendarDays.reduce<Record<string, CalendarOrder[]>>((acc, day) => {
    const dayKey = day.format('YYYY-MM-DD')
    acc[dayKey] = ordersWithDueDate.filter(
      (order) => dayjs(order.fulfillment_due_date).format('YYYY-MM-DD') === dayKey
    )
    return acc
  }, {})

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className="flex flex-col gap-4">
      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[900px]">
          {/* Day Headers */}
          {dayNames.map((dayName) => (
            <div
              key={dayName}
              className="p-2 text-center text-sm font-medium text-muted-foreground"
            >
              {dayName}
            </div>
          ))}

          {/* Calendar Days */}
          {calendarDays.map((day) => {
            const dayKey = day.format('YYYY-MM-DD')
            const dayOrders = ordersByDay[dayKey] || []
            const dayOrderCount = orderCountsByDay[dayKey] ?? 0
            const isCurrentMonth = day.isSame(startOfMonth, 'month')
            const isToday = day.isSame(today, 'day')
            const isWeekend = day.day() === 0 || day.day() === 6

            return (
              <div
                key={dayKey}
                className={cn(
                  'min-h-[120px] rounded-md border border-border/65 bg-card p-1',
                  !isCurrentMonth && 'border-border/40 bg-elevated/45',
                  isWeekend && isCurrentMonth && 'bg-elevated/70 dark:bg-elevated'
                )}
              >
                {/* Date Number */}
                <Button
                  asChild
                  variant={isToday ? 'default' : isCurrentMonth ? 'ghost' : 'ghostMuted'}
                  size="rowCompact"
                  className="mb-1 w-full justify-start text-left"
                >
                  <Link
                    {...getDayGridLinkProps(dayKey)}
                    aria-label={isToday ? `${day.format('D')} Today` : undefined}
                  >
                    <span className="leading-none">{day.format('D')}</span>
                    {isToday ? (
                      <span className="text-[11px] leading-none font-semibold">Today</span>
                    ) : null}
                  </Link>
                </Button>

                {/* Orders for this day */}
                <div className="space-y-1">
                  {isLoading && isCurrentMonth ? (
                    <Skeleton className="h-6" />
                  ) : showOrderCounts && dayOrderCount > 0 ? (
                    <MonthOrderCountLink count={dayOrderCount} dayKey={dayKey} />
                  ) : dayOrders?.length > 0 ? (
                    dayOrders
                      .slice(0, 2)
                      .map((order) => <MonthOrderCard key={order.id} order={order} />)
                  ) : null}

                  {/* Show "+X more" if there are more than 3 orders */}
                  {!showOrderCounts && dayOrders?.length > 2 && (
                    <div className="py-1 text-center">
                      <Link {...getDayGridLinkProps(dayKey)}>
                        <Typography variant="caption" interaction="hoverUnderline">
                          +{dayOrders?.length - 2} more
                        </Typography>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
